import { ArrowDown, ArrowUp, Eye, Plus, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AdminCard, AdminHeader, Field } from "./AdminShell";

type Section = {
  id?: number;
  section_key?: string;
  section_type: string;
  title?: string;
  subtitle?: string;
  image_url?: string;
  button_text?: string;
  button_link?: string;
  background_color?: string;
  config_json?: string;
  sort_order?: number;
  is_active?: number | boolean;
};

const blank: Section = {
  section_type: "custom_image",
  title: "",
  subtitle: "",
  image_url: "",
  button_text: "",
  button_link: "",
  background_color: "#FFFFFF",
  config_json: "{}",
  sort_order: 100,
  is_active: true
};

const sectionTypes = ["hero", "banner", "category_grid", "featured_products", "product_collection", "promo_image", "image_text", "benefits", "newsletter", "custom_image", "custom_html", "footer_links"];

export default function Homepage() {
  const [sections, setSections] = useState<Section[]>([]);
  const [editing, setEditing] = useState<Section>(blank);
  const [message, setMessage] = useState("");

  async function load() {
    setSections(await api<Section[]>("/api/admin/homepage/sections"));
  }

  useEffect(() => { load().catch(() => setSections([])); }, []);

  async function save() {
    const path = editing.id ? `/api/admin/homepage/sections/${editing.id}` : "/api/admin/homepage/sections";
    await api(path, { method: editing.id ? "PUT" : "POST", body: JSON.stringify(editing) });
    setEditing(blank);
    setMessage("Homepage section saved.");
    await load();
  }

  async function remove(id?: number) {
    if (!id || !confirm("Delete this homepage section?")) return;
    await api(`/api/admin/homepage/sections/${id}`, { method: "DELETE" });
    await load();
  }

  async function move(id: number | undefined, direction: "up" | "down") {
    if (!id) return;
    await api(`/api/admin/homepage/sections/${id}/move`, { method: "PUT", body: JSON.stringify({ direction }) });
    await load();
  }

  return (
    <div>
      <AdminHeader
        title="Homepage Sections"
        description="Customize the storefront from top to bottom. Add hero blocks, image modules, category grids, product sections, benefits, newsletter blocks, custom HTML, and footer links."
        action={<button className="btn-primary" onClick={() => setEditing(blank)}><Plus size={16} className="mr-2" />New Section</button>}
      />
      {message ? <div className="mb-5 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{message}</div> : null}
      <div className="grid gap-6 xl:grid-cols-[1fr_430px]">
        <AdminCard>
          <div className="space-y-3">
            {sections.map((section) => (
              <div key={section.id} className="grid gap-4 rounded-[24px] border border-[#0F172A]/10 bg-[#FDF9F1] p-4 md:grid-cols-[92px_1fr_auto] md:items-center">
                <img src={section.image_url || "/placeholder.webp"} className="h-24 w-full rounded-2xl object-cover md:w-24" />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-[#0F172A] px-3 py-1 text-xs font-black text-white">{section.section_type}</span>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500">Order {section.sort_order ?? 0}</span>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${section.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{section.is_active ? "Active" : "Hidden"}</span>
                  </div>
                  <h2 className="mt-3 text-xl font-black">{section.title || "Untitled section"}</h2>
                  <p className="mt-1 text-sm text-slate-500">{section.subtitle || "No subtitle"}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button className="btn-secondary px-4 py-2" onClick={() => move(section.id, "up")}><ArrowUp size={15} /></button>
                  <button className="btn-secondary px-4 py-2" onClick={() => move(section.id, "down")}><ArrowDown size={15} /></button>
                  <button className="btn-secondary px-4 py-2" onClick={() => setEditing(section)}><Eye size={15} /></button>
                  <button className="btn-secondary px-4 py-2 text-red-600" onClick={() => remove(section.id)}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {!sections.length ? <p className="rounded-[24px] bg-[#FDF9F1] p-6 text-sm text-slate-500">No custom sections yet. The storefront will use the default Premium Soft Commerce homepage.</p> : null}
          </div>
        </AdminCard>
        <AdminCard title={editing.id ? "Edit Section" : "Add Section"}>
          <div className="space-y-4">
            <Field label="Section Type"><select className="input" value={editing.section_type} onChange={(e) => setEditing({ ...editing, section_type: e.target.value })}>{sectionTypes.map((type) => <option key={type}>{type}</option>)}</select></Field>
            <Field label="Title"><input className="input" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></Field>
            <Field label="Subtitle"><textarea className="input min-h-24 rounded-[24px]" value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></Field>
            <Field label="Image URL"><input className="input" value={editing.image_url || ""} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} /></Field>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Button Text"><input className="input" value={editing.button_text || ""} onChange={(e) => setEditing({ ...editing, button_text: e.target.value })} /></Field>
              <Field label="Button Link"><input className="input" value={editing.button_link || ""} onChange={(e) => setEditing({ ...editing, button_link: e.target.value })} /></Field>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Background"><input className="input" value={editing.background_color || ""} onChange={(e) => setEditing({ ...editing, background_color: e.target.value })} /></Field>
              <Field label="Sort Order"><input className="input" type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} /></Field>
            </div>
            <Field label="Config JSON"><textarea className="input min-h-24 rounded-[24px] font-mono" value={editing.config_json || "{}"} onChange={(e) => setEditing({ ...editing, config_json: e.target.value })} /></Field>
            <label className="flex items-center gap-3 text-sm font-bold"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Enabled</label>
            <button className="btn-primary w-full" onClick={save}><Save size={16} className="mr-2" />Save Configuration</button>
          </div>
        </AdminCard>
      </div>
    </div>
  );
}
